import React, { FC, useState, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { Modal, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import {
  GetGoogleLinkedAccountsResponse,
  GetAgentWhatsappNumberResponse,
  GoogleLinkedAccount,
} from '@dreamai/types';
import { ReactComponent as GoogleLogo } from '../../icons/google-logo.svg';
import { ReactComponent as WhatsappIcon } from '../../icons/whatsapp.svg';
import { ReactComponent as ChatBubbleDotsIcon } from '../../icons/chat-bubble-dots.svg';
import { ReactComponent as TodoistLogo } from '../../icons/todoist-logo.svg';
import xeroLogo from '../../icons/xero.png';
import granolaLogo from '../../icons/granola.png';
import { ReactComponent as SlackLogo } from '../../icons/slack-logo.svg';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAgent } from '../../contexts/AgentContext';
import api from '../../api';
import WhatsAppBridgeCard from './WhatsAppBridgeCard';
import './connected-accounts-section.scss';

const ConnectedAccountsSection: FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { agents } = useAgent();
  const assistantName =
    agents.find((a) => (a.agent_type as string) === 'Personal Assistant')
      ?.nickname || 'your Assistant';
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<GoogleLinkedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // WhatsApp state
  const { messagingState, refreshStatus } = useMessaging();
  const [whatsappNumber, setWhatsappNumber] = useState<string | null>(null);
  const [whatsappLoading, setWhatsappLoading] = useState(true);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [regCodeLoading, setRegCodeLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [regCodeError, setRegCodeError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Todoist state
  const [todoistAccounts, setTodoistAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [todoistLoading, setTodoistLoading] = useState(true);

  // Xero state
  const [xeroAccounts, setXeroAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [xeroLoading, setXeroLoading] = useState(true);

  // Granola state
  const [granolaAccounts, setGranolaAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [granolaLoading, setGranolaLoading] = useState(true);
  const [granolaApiKey, setGranolaApiKey] = useState('');
  const [granolaConnecting, setGranolaConnecting] = useState(false);

  // Slack state
  const [slackAccounts, setSlackAccounts] = useState<
    { id: number; workspace: string; scope: string }[]
  >([]);
  const [slackLoading, setSlackLoading] = useState(true);

  const fetchAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetGoogleLinkedAccountsResponse>(
        '/google-link',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching linked accounts:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchWhatsappNumber = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetAgentWhatsappNumberResponse>(
        '/agent-whatsapp/number',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWhatsappNumber(res.data.whatsapp_number || null);
    } catch (err) {
      console.error('Error fetching WhatsApp number:', err);
    } finally {
      setWhatsappLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchTodoistAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/todoist-link', { headers: { Authorization: `Bearer ${token}` } });
      setTodoistAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Todoist accounts:', err);
    } finally {
      setTodoistLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchXeroAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/xero-link', { headers: { Authorization: `Bearer ${token}` } });
      setXeroAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Xero accounts:', err);
    } finally {
      setXeroLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchGranolaAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/granola-link', { headers: { Authorization: `Bearer ${token}` } });
      setGranolaAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Granola accounts:', err);
    } finally {
      setGranolaLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchSlackAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; workspace: string; scope: string }[];
      }>('/slack-user-link', { headers: { Authorization: `Bearer ${token}` } });
      setSlackAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Slack accounts:', err);
    } finally {
      setSlackLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchAccounts();
    fetchWhatsappNumber();
    fetchTodoistAccounts();
    fetchXeroAccounts();
    fetchGranolaAccounts();
    fetchSlackAccounts();
  }, [
    fetchAccounts,
    fetchWhatsappNumber,
    fetchTodoistAccounts,
    fetchXeroAccounts,
    fetchGranolaAccounts,
    fetchSlackAccounts,
  ]);

  // Refetch WhatsApp number when messaging state changes
  useEffect(() => {
    if (messagingState.whatsapp.loaded) {
      fetchWhatsappNumber();
    }
  }, [messagingState.whatsapp.connected]);

  // Close modal when WhatsApp becomes connected
  useEffect(() => {
    if (messagingState.whatsapp.connected && showWhatsappModal) {
      setShowWhatsappModal(false);
      setFeedback({
        type: 'success',
        message: 'WhatsApp connected successfully',
      });
      setTimeout(() => setFeedback(null), 3000);
    }
  }, [messagingState.whatsapp.connected, showWhatsappModal]);

  // Handle return from OAuth callback
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      fetchAccounts();
      fetchTodoistAccounts();
      fetchXeroAccounts();
      setFeedback({
        type: 'success',
        message: 'Account connected successfully',
      });
      setTimeout(() => setFeedback(null), 3000);
    } else if (status === 'error') {
      setFeedback({
        type: 'error',
        message: 'Failed to connect account. Please try again.',
      });
    }
  }, []);

  const handleConnectAccount = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      scope: 'email calendar',
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/google-link/initiate-google-oauth?${params}`;
  }, [user]);

  const handleRemoveAccount = useCallback(
    async (oauthId: number) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/google-link/${oauthId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Account disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err: any) {
        const errorData = err?.response?.data;
        if (errorData?.error === 'Account in use') {
          setFeedback({
            type: 'error',
            message: `This account is in use by ${errorData.profiles.length} profile(s) and cannot be removed.`,
          });
        } else {
          console.error('Error removing account:', err);
          setFeedback({
            type: 'error',
            message: 'Failed to disconnect account.',
          });
        }
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectWhatsapp = useCallback(async () => {
    setShowWhatsappModal(true);
    setRegCodeLoading(true);
    setRegCodeError(null);
    setRegistrationData(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/agent-whatsapp/registration-code',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRegistrationData(response.data);
    } catch (err) {
      console.error('Error getting WhatsApp registration code:', err);
      setRegCodeError(
        'Failed to generate registration code. Please try again.',
      );
    } finally {
      setRegCodeLoading(false);
    }
  }, [getAccessTokenSilently]);

  const handleConnectTodoist = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/todoist-link/initiate-todoist-oauth?${params}`;
  }, [user]);

  const handleConnectXero = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/xero-link/initiate-xero-oauth?${params}`;
  }, [user]);

  const handleRemoveXero = useCallback(
    async (oauthId: number) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/xero-link/${oauthId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setXeroAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Xero disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Xero account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Xero.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveTodoist = useCallback(
    async (oauthId: number) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/todoist-link/${oauthId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTodoistAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Todoist disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Todoist account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Todoist.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectGranola = useCallback(async () => {
    if (!granolaApiKey.trim()) return;
    setGranolaConnecting(true);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/granola-link/connect',
        { apiKey: granolaApiKey.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setGranolaApiKey('');
      await fetchGranolaAccounts();
      setFeedback({ type: 'success', message: 'Granola connected' });
      setTimeout(() => setFeedback(null), 3000);
    } catch (err: any) {
      console.error('Error connecting Granola account:', err);
      setFeedback({
        type: 'error',
        message:
          err?.response?.data?.error ||
          'Failed to connect Granola. Check your API key.',
      });
    } finally {
      setGranolaConnecting(false);
    }
  }, [granolaApiKey, getAccessTokenSilently, fetchGranolaAccounts]);

  const handleConnectSlack = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/slack-user-link/initiate?${params}`;
  }, [user]);

  const handleRemoveSlack = useCallback(
    async (oauthId: number) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/slack-user-link/${oauthId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSlackAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Slack disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Slack account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Slack.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveGranola = useCallback(
    async (oauthId: number) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(`/granola-link/${oauthId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGranolaAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Granola disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Granola account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Granola.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleDisconnectWhatsapp = useCallback(async () => {
    setDisconnecting(true);
    try {
      const token = await getAccessTokenSilently();
      await api.delete('/agent-whatsapp/connection', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWhatsappNumber(null);
      await refreshStatus();
      setFeedback({ type: 'success', message: 'WhatsApp disconnected' });
      setTimeout(() => setFeedback(null), 3000);
    } catch (err) {
      console.error('Error disconnecting WhatsApp:', err);
      setFeedback({ type: 'error', message: 'Failed to disconnect WhatsApp.' });
    } finally {
      setDisconnecting(false);
    }
  }, [getAccessTokenSilently, refreshStatus]);

  return (
    <div className='connected-accounts-section'>
      <div className='connected-accounts-header'>
        <h2 className='section-title'>Connected accounts</h2>
        <p className='connected-accounts-subtitle'>
          Connect your email and calendar accounts so {assistantName} can help
          manage your inbox and schedule.
        </p>
      </div>

      {feedback && (
        <p className={`connected-accounts-feedback ${feedback.type}`}>
          {feedback.message}
        </p>
      )}

      <div className='connected-accounts-card'>
        {loading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {accounts.map((account) => (
              <div key={account.id} className='connected-account-row'>
                <div className='account-info'>
                  <GoogleLogo className='account-provider-icon' />
                  <span className='account-email'>{account.email}</span>
                  {account.scope && (
                    <span className='account-scope'>{account.scope}</span>
                  )}
                </div>
                <button
                  className='disconnect-btn'
                  onClick={() => handleRemoveAccount(account.id)}
                  aria-label={`Disconnect ${account.email}`}
                >
                  Disconnect
                </button>
              </div>
            ))}

            <button
              className='connect-account-btn'
              onClick={handleConnectAccount}
            >
              <GoogleLogo className='google-logo' />
              <span>Connect a Google account</span>
            </button>

            <p className='connected-accounts-hint'>
              We only support Gmail and Google Workspace accounts right now.
            </p>
          </>
        )}
      </div>

      {/* WhatsApp Section — Two-column layout */}
      <h3 className='wa-section-header'>WhatsApp</h3>
      <div className='whatsapp-columns'>
        {/* Left: Chat to assistant on WhatsApp (Twilio notifications) */}
        <div className='wa-column-card'>
          <ChatBubbleDotsIcon className='wa-column-icon chat-icon' />
          <h4 className='wa-column-title'>
            Chat to {assistantName} on WhatsApp
          </h4>
          <p className='wa-column-description'>
            Chat and receive messages from {assistantName} on WhatsApp
          </p>

          {whatsappLoading ? (
            <div className='settings-skeleton' />
          ) : messagingState.whatsapp.connected && whatsappNumber ? (
            <div className='wa-connected-row'>
              <div className='wa-connected-info'>
                <WhatsappIcon className='wa-connected-icon' />
                <span className='wa-connected-label'>{whatsappNumber}</span>
                <span className='wa-connected-scope'>WhatsApp</span>
              </div>
              <button
                className='wa-disconnect-btn'
                onClick={handleDisconnectWhatsapp}
                disabled={disconnecting}
                aria-label='Disconnect WhatsApp'
              >
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <circle
                    cx='12'
                    cy='12'
                    r='9'
                    stroke='currentColor'
                    strokeWidth='1.5'
                  />
                  <line
                    x1='8'
                    y1='12'
                    x2='16'
                    y2='12'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                  />
                </svg>
              </button>
            </div>
          ) : (
            <button
              className='wa-activate-btn wa-connect-btn'
              onClick={handleConnectWhatsapp}
            >
              Chat on WhatsApp
            </button>
          )}
        </div>

        {/* Right: WhatsApp Co-Pilot (Bridge pairing) */}
        <WhatsAppBridgeCard />
      </div>

      {/* Todoist Section */}
      <div className='connected-accounts-card'>
        {todoistLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {todoistAccounts.map((account) => (
              <div key={account.id} className='connected-account-row'>
                <div className='account-info'>
                  <TodoistLogo className='account-provider-icon' />
                  <span className='account-email'>{account.email}</span>
                  <span className='account-scope'>{account.scope}</span>
                </div>
                <button
                  className='disconnect-btn'
                  onClick={() => handleRemoveTodoist(account.id)}
                  aria-label={`Disconnect ${account.email}`}
                >
                  Disconnect
                </button>
              </div>
            ))}

            {todoistAccounts.length === 0 && (
              <>
                <button
                  className='connect-account-btn todoist-connect-btn'
                  onClick={handleConnectTodoist}
                >
                  <TodoistLogo className='google-logo' />
                  <span>Connect Todoist</span>
                </button>
                <p className='connected-accounts-hint'>
                  Connect your Todoist account so {assistantName} can manage
                  reminders and tasks for you.
                </p>
              </>
            )}
          </>
        )}
      </div>

      {/* Xero Section */}
      <div className='connected-accounts-card'>
        {xeroLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {xeroAccounts.map((account) => (
              <div key={account.id} className='connected-account-row'>
                <div className='account-info'>
                  <img
                    src={xeroLogo}
                    alt='Xero'
                    className='account-provider-icon'
                  />
                  <span className='account-email'>{account.email}</span>
                  <span className='account-scope'>{account.scope}</span>
                </div>
                <button
                  className='disconnect-btn'
                  onClick={() => handleRemoveXero(account.id)}
                  aria-label={`Disconnect ${account.email}`}
                >
                  Disconnect
                </button>
              </div>
            ))}

            {xeroAccounts.length === 0 && (
              <>
                <button
                  className='connect-account-btn xero-connect-btn'
                  onClick={handleConnectXero}
                >
                  <img src={xeroLogo} alt='Xero' className='google-logo' />
                  <span>Connect Xero</span>
                </button>
                <p className='connected-accounts-hint'>
                  Connect your Xero account so {assistantName} can manage
                  transactions, receipts, and financial reports.
                </p>
              </>
            )}
          </>
        )}
      </div>

      {/* Granola Section */}
      <div className='connected-accounts-card'>
        {granolaLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {granolaAccounts.map((account) => {
              const displayName =
                account.email && account.email.includes('@')
                  ? account.email
                  : 'Granola account';
              return (
                <div key={account.id} className='connected-account-row'>
                  <div className='account-info'>
                    <img
                      src={granolaLogo}
                      alt='Granola'
                      className='account-provider-icon'
                    />
                    <span className='account-email'>{displayName}</span>
                    <span className='account-scope'>{account.scope}</span>
                  </div>
                  <button
                    className='disconnect-btn'
                    onClick={() => handleRemoveGranola(account.id)}
                    aria-label={`Disconnect ${displayName}`}
                  >
                    Disconnect
                  </button>
                </div>
              );
            })}

            {granolaAccounts.length === 0 && (
              <>
                <div className='granola-intro'>
                  <img
                    src={granolaLogo}
                    alt='Granola'
                    className='account-provider-icon'
                  />
                  <span className='granola-intro-title'>Granola</span>
                </div>
                <div className='granola-key-form'>
                  <input
                    type='password'
                    className='granola-key-input'
                    placeholder='Paste your Granola API key (grn_…)'
                    value={granolaApiKey}
                    onChange={(e) => setGranolaApiKey(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleConnectGranola();
                    }}
                    autoComplete='off'
                  />
                  <button
                    className='connect-account-btn'
                    onClick={handleConnectGranola}
                    disabled={granolaConnecting || !granolaApiKey.trim()}
                  >
                    <span>
                      {granolaConnecting ? 'Connecting…' : 'Connect Granola'}
                    </span>
                  </button>
                </div>
                <p className='connected-accounts-hint'>
                  Connect Granola so {assistantName} can read your meeting notes
                  and call transcripts. Generate an API key in the Granola
                  desktop app under Settings → Connectors → API keys, then paste
                  it here.
                </p>
              </>
            )}
          </>
        )}
      </div>

      {/* Slack Section */}
      <div className='connected-accounts-card'>
        {slackLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {slackAccounts.map((account) => (
              <div key={account.id} className='connected-account-row'>
                <div className='account-info'>
                  <SlackLogo className='account-provider-icon' />
                  <span className='account-email'>{account.workspace}</span>
                  <span className='account-scope'>Slack</span>
                </div>
                <button
                  className='disconnect-btn'
                  onClick={() => handleRemoveSlack(account.id)}
                  aria-label={`Disconnect ${account.workspace}`}
                >
                  Disconnect
                </button>
              </div>
            ))}

            {slackAccounts.length === 0 && (
              <>
                <button
                  className='connect-account-btn slack-connect-btn'
                  onClick={handleConnectSlack}
                >
                  <SlackLogo className='google-logo' />
                  <span>Connect Slack</span>
                </button>
                <p className='connected-accounts-hint'>
                  Connect your Slack account so {assistantName} can read your
                  messages and search your workspace.
                </p>
              </>
            )}
          </>
        )}
      </div>

      {/* WhatsApp Registration Code Modal */}
      <Modal
        show={showWhatsappModal}
        onHide={() => setShowWhatsappModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Connect WhatsApp</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='whatsapp-reg-modal'>
            {regCodeLoading ? (
              <div className='whatsapp-reg-loading'>
                <Spinner animation='border' size='sm' />
                <p>Generating registration code...</p>
              </div>
            ) : regCodeError ? (
              <div className='whatsapp-reg-error'>{regCodeError}</div>
            ) : registrationData ? (
              <div className='whatsapp-reg-content'>
                <QRCodeSVG value={registrationData.whatsapp_url} size={200} />
                <p className='whatsapp-reg-code'>
                  Registration Code: <strong>{registrationData.code}</strong>
                </p>
                <p className='whatsapp-reg-instructions'>
                  Scan this QR code with your phone to open WhatsApp and send
                  the registration code. Or manually send the code to our
                  WhatsApp number.
                </p>
                <p className='whatsapp-reg-expiry'>Code expires in 24 hours.</p>
              </div>
            ) : null}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ConnectedAccountsSection;
